import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Gem, Coins } from 'lucide-react';
import { ConnectButton } from '../ConnectButton';
import dengsLogo from '../../assets/dengs.png';
import { useTokenPrices } from '../../hooks/useTokenPrices';

interface NavbarProps {
  currentPage: string;
  setCurrentPage: (page: string) => void;
}

export function Navbar({ currentPage, setCurrentPage }: NavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { apePrice, gemPrice, isLoading } = useTokenPrices();

  const navItems = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'about', label: 'About $GEM' },
  ];

  const handleNavItemClick = (id: string) => {
    setCurrentPage(id);
    setIsOpen(false);
  };

  const formatApePrice = (price: number) => price.toFixed(2);
  const formatGemPrice = (price: number) => price.toFixed(6);
  const formatChange = (change: number) => change > 0 ? `+${change.toFixed(2)}%` : `${change.toFixed(2)}%`;

  return (
    <nav className="fixed w-full z-50 bg-slate-950/95 backdrop-blur-md border-b border-blue-500/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <motion.button
            onClick={() => handleNavItemClick("dashboard")}
            className="flex items-center space-x-2 focus:outline-none"
            whileTap={{ scale: 0.95 }}
          >
            <motion.img 
              src={dengsLogo} 
              alt="DengSniper Logo" 
              className="w-8 h-8 object-contain"
              whileHover={{ 
                rotate: [0, -10, 10, -10, 10, 0],
                transition: { duration: 0.6 }
              }}
            />
            <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-blue-600 text-transparent bg-clip-text">
              DengSniper
            </span>
          </motion.button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-6">
            {/* Token Prices */}
            {!isLoading && (
              <div className="flex items-center space-x-4">
                {apePrice && (
                  <div className="flex items-center gap-1.5 text-sm">
                    <span className="text-gray-400">$APE</span>
                    <span className="text-white">${formatApePrice(apePrice.price)}</span>
                    <span className={`${apePrice.change24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatChange(apePrice.change24h)}
                    </span>
                  </div>
                )}
                {gemPrice && (
                  <div className="flex items-center gap-1.5 text-sm">
                    <span className="text-gray-400">$GEM</span>
                    <span className="text-white">${formatGemPrice(gemPrice.price)}</span>
                    <span className={`${gemPrice.change24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatChange(gemPrice.change24h)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Navigation Items */}
            {navItems.map((item) => (
              <motion.button
                key={item.id}
                onClick={() => handleNavItemClick(item.id)}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  currentPage === item.id
                    ? "text-white bg-blue-500/10 ring-1 ring-blue-500/20"
                    : "text-gray-300 hover:text-white hover:bg-blue-500/5"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {item.label}
              </motion.button>
            ))}

            {/* Connect Button */}
            <ConnectButton />
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-lg text-gray-400 hover:text-white hover:bg-blue-500/10 focus:outline-none"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Main menu"
              aria-expanded={isOpen}
            >
              {isOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="md:hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="px-4 pt-2 pb-3 space-y-2 border-t border-blue-500/20">
              {/* Token Prices - Mobile */}
              {!isLoading && (
                <div className="grid grid-cols-2 gap-2 py-2">
                  {apePrice && (
                    <div className="bg-blue-500/10 rounded-lg p-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">$APE</span>
                        <span className="text-white text-sm">${formatApePrice(apePrice.price)}</span>
                      </div>
                      <div className={`text-xs mt-1 ${apePrice.change24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatChange(apePrice.change24h)}
                      </div>
                    </div>
                  )}
                  {gemPrice && (
                    <div className="bg-blue-500/10 rounded-lg p-2">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">$GEM</span>
                        <span className="text-white text-sm">${formatGemPrice(gemPrice.price)}</span>
                      </div>
                      <div className={`text-xs mt-1 ${gemPrice.change24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatChange(gemPrice.change24h)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Navigation Items */}
              {navItems.map((item) => (
                <motion.button
                  key={item.id}
                  onClick={() => handleNavItemClick(item.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg text-base font-medium ${
                    currentPage === item.id
                      ? "text-white bg-blue-500/10"
                      : "text-gray-300 hover:text-white hover:bg-blue-500/5"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {item.label}
                </motion.button>
              ))}
              <div className="pt-2 px-2">
                <ConnectButton />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}